import { enviroment } from "../../enviroment";
import {
  readConsent,
  readInternalMarker,
  writeInternalMarker,
} from "./clarityConsent";
import type { ClarityIdentity } from "./clarityIdentity";

// The ONE Clarity facade (design DESIGN-CLARITY 22/9, section 2.2). Every
// call is try/catch and no function throws; nothing reads window/document
// at module level. Two flags:
//  - REACT_APP_CLARITY_CONSENT_ENABLED gates the consent functions
//    (activate/consent/stop/cookies) - they talk to the bootstrap object
//    window.__kmAnalytics that public/index.html defines with the flag on.
//  - REACT_APP_CLARITY_ENABLED gates tags/identify; REACT_APP_CLARITY_
//    EVENTS_ENABLED additionally gates the events.
// Values sent as tags are always String(...) of ids/codes - never names,
// e-mails, phone numbers or free text.

declare global {
  interface Window {
    __kmAnalytics?: {
      state: string;
      noAnalyticsRoute: boolean;
      load: () => void;
      isLoaded: () => boolean;
    };
  }
}

export type { ClarityIdentity, ClarityRole } from "./clarityIdentity";
export type { ClarityConsentState } from "./clarityConsent";

export type ClarityTag =
  | "forhandler"
  | "kunde"
  | "rolle"
  | "sprog"
  | "km_session"
  | "fejlkode"
  | "cwv_daarlig";

export const ClarityEvents = {
  LOGO_UPLOADED: "logo_uploadet",
  LOGO_CREATED: "logo_oprettet",
  DESIGN_SAVED: "design_gemt",
  ADD_TO_ORDER: "tilfoej_til_ordre",
  EXCEL_UPLOAD: "excel_upload",
  CART_CONTINUE: "kurv_fortsaet",
  ORDER_PLACED: "ordre_afgivet",
  ERROR_SHOWN: "fejl_vist",
  // The "Reduced functionality: the logo supplier is not responding" banner
  // was shown (once per episode per tab session, SupplierStatusBanner) -
  // Clarity's 28 dead clicks on it can then be read against how often it
  // is on screen at all.
  SUPPLIER_DEGRADED_SHOWN: "leverandoer_nede_vist",
} as const;
export type ClarityEventName =
  (typeof ClarityEvents)[keyof typeof ClarityEvents];

// Cookies the two vendors set; "_ga_<id>" is added from the measurement id.
const ANALYTICS_COOKIE_NAMES = ["_ga", "_gid", "_clck", "_clsk"] as const;

// --- internals -------------------------------------------------------------

// Page-bound dedupe for set/identify: Clarity treats every SPA route change
// as a new page and custom tags/identify are per page, so the cache is
// dropped whenever the pathname differs from the one it was built for.
// Re-renders and StrictMode double runs on the same page send nothing twice.
let dedupePathname: string | null = null;
const sentOnPage = new Set<string>();
let lastIdentity: ClarityIdentity | null = null;
let loadRequested = false;
// Set by clarityConsent(false) and stopAnalytics(), cleared by
// clarityConsent(true): while true the GA4 facade (trackEvent/
// trackPageView) sends nothing, and Google's documented kill-switch
// window["ga-disable-<id>"] silences gtag.js' own hits (enhanced
// measurement) as well. Only the flagged consent/stop paths ever set it, so
// with the flags off the GA4 facade behaves exactly as before.
let analyticsStopped = false;
// clarityConsent(false) ran in this page view: a recorder that was stopped
// starts again only on the next page load (the dialog shows the hint).
let withdrawnThisPageView = false;

function currentPathname(): string {
  try {
    return window.location.pathname;
  } catch {
    return "";
  }
}

function alreadySent(key: string): boolean {
  const pathname = currentPathname();
  if (dedupePathname !== pathname) {
    sentOnPage.clear();
    dedupePathname = pathname;
  }
  if (sentOnPage.has(key)) return true;
  sentOnPage.add(key);
  return false;
}

function callClarity(...args: unknown[]): void {
  try {
    if (typeof window.clarity === "function") {
      window.clarity(...args);
    }
  } catch {
    // Analytics must never break the app.
  }
}

// gtag.js is only loaded inside load(); before that window.gtag is
// undefined and a consent push straight into dataLayer is what gtag.js
// picks up later. It must be an Arguments object, not an array.
function callGtag(..._args: unknown[]): void {
  try {
    if (typeof window.gtag === "function") {
      window.gtag(..._args);
      return;
    }
    window.dataLayer = window.dataLayer || [];
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  } catch {
    // Ignore.
  }
}

function isTagLoaded(): boolean {
  try {
    const bootstrap = window.__kmAnalytics;
    if (bootstrap) return bootstrap.isLoaded();
    // No bootstrap object = consent flag off = the tags loaded at once.
    return typeof window.clarity === "function";
  } catch {
    return false;
  }
}

// Google's kill-switch: window["ga-disable-<measurement id>"] = true makes
// an already loaded gtag.js drop every hit for that property (checked per
// hit, so it also works after the library is loaded). Removed again when
// consent is (re-)granted.
function setGaDisabled(disabled: boolean): void {
  try {
    const id = enviroment.ga4MeasurementId;
    if (!id) return;
    const key = "ga-disable-" + id;
    const w = window as unknown as Record<string, unknown>;
    if (disabled) w[key] = true;
    else delete w[key];
  } catch {
    // Ignore.
  }
}

function setStopped(stopped: boolean): void {
  analyticsStopped = stopped;
  setGaDisabled(stopped);
}

// Read by the GA4 facade (src/utils/functions/analytics.ts): true after a
// withdrawal or a stop in this page view until consent is granted again.
export function isAnalyticsStopped(): boolean {
  return analyticsStopped;
}

export function wasConsentWithdrawnThisPageView(): boolean {
  return withdrawnThisPageView;
}

// --- consent (requires ONLY enviroment.clarityConsentEnabled) --------------

// The internal marker (last user of this browser was internal, RECORD_
// INTERNAL off) defers the load until applyIdentity knows better - the
// bootstrap in index.html skips the load for the same marker.
export function activateAnalytics(): void {
  if (!enviroment.clarityConsentEnabled) return;
  try {
    if (!loadRequested && !readInternalMarker()) {
      loadRequested = true;
      window.__kmAnalytics?.load?.();
    }
  } catch {
    // Ignore.
  }
  clarityConsent(true);
}

export function clarityConsent(granted: boolean): void {
  if (!enviroment.clarityConsentEnabled) return;
  if (granted) {
    setStopped(false);
    callClarity("consentv2", {
      ad_Storage: "denied",
      analytics_Storage: "granted",
    });
    callGtag("consent", "update", { analytics_storage: "granted" });
    return;
  }
  setStopped(true);
  callClarity("consentv2", {
    ad_Storage: "denied",
    analytics_Storage: "denied",
  });
  // Only a loaded tag is told to stop: before the load window.clarity is
  // the bootstrap's queue stub, and a queued ("consent", false) + ("stop")
  // would be replayed by the tag if the user accepts later in the same
  // page view - the recorder would then start stopped.
  if (isTagLoaded()) {
    withdrawnThisPageView = true;
    callClarity("consent", false);
    callClarity("stop");
  }
  callGtag("consent", "update", { analytics_storage: "denied" });
  deleteAnalyticsCookies();
}

// Stored "granted" -> load (idempotent) + signals; "denied"/"unset" ->
// nothing (the tag is not loaded; there is nothing to signal).
export function applyStoredConsent(): void {
  if (!enviroment.clarityConsentEnabled) return;
  if (readConsent() === "granted") activateAnalytics();
}

// Route guard (/public*, /approval*) and internal users: stop the recorder
// if the tag is loaded. Deletes NO cookies - the stored choice stands.
export function stopAnalytics(): void {
  if (!enviroment.clarityConsentEnabled && !enviroment.clarityEnabled) return;
  if (!isTagLoaded()) return;
  setStopped(true);
  callClarity("stop");
  callGtag("consent", "update", { analytics_storage: "denied" });
}

// _ga, _ga_<measurement id without "G-">, _gid, _clck, _clsk on the host,
// "."+hostname and "."+the last two labels. Errors are ignored.
export function deleteAnalyticsCookies(): void {
  if (!enviroment.clarityConsentEnabled) return;
  try {
    const names: string[] = [...ANALYTICS_COOKIE_NAMES];
    const measurementId = (enviroment.ga4MeasurementId || "").replace(
      /^G-/,
      "",
    );
    if (measurementId) names.push("_ga_" + measurementId);
    const hostname = window.location.hostname || "";
    const labels = hostname.split(".").filter(Boolean);
    const domains: (string | undefined)[] = [undefined];
    if (hostname) domains.push("." + hostname);
    if (labels.length > 2) domains.push("." + labels.slice(-2).join("."));
    for (const name of names) {
      for (const domain of domains) {
        try {
          document.cookie =
            name +
            "=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/" +
            (domain ? "; domain=" + domain : "");
        } catch {
          // Ignore.
        }
      }
    }
  } catch {
    // Ignore.
  }
}

// --- identity / tags (require enviroment.clarityEnabled) -------------------

// With the consent flag on the bootstrap defines the window.clarity queue
// stub before any choice is made, so tags/identify/events are additionally
// gated on a LOADED tag: nothing is built up or queued for a user who has
// not accepted (or has declined), and the dedupe cache is only marked once
// the calls really go out. Accept then sends everything fresh
// (activateAnalytics -> resendIdentity).
export function isClarityEnabled(): boolean {
  try {
    if (!enviroment.clarityEnabled) return false;
    if (typeof window.clarity !== "function") return false;
    return !enviroment.clarityConsentEnabled || isTagLoaded();
  } catch {
    return false;
  }
}

export function claritySet(tag: ClarityTag, value: string | string[]): void {
  if (!isClarityEnabled()) return;
  const key = "set:" + tag + "=" + (Array.isArray(value) ? value.join("|") : value);
  if (alreadySent(key)) return;
  callClarity("set", tag, value);
}

export function clarityIdentify(customId: string, friendlyName?: string): void {
  if (!isClarityEnabled()) return;
  if (alreadySent("identify:" + customId + "|" + (friendlyName ?? ""))) return;
  callClarity("identify", customId, undefined, undefined, friendlyName);
}

// Remembers the identity; an internal user without RECORD_INTERNAL is
// stopped and gets neither tags nor identify.
export function applyIdentity(identity: ClarityIdentity): void {
  lastIdentity = identity;
  if (!enviroment.clarityEnabled) return;
  if (identity.rolle === "intern" && !enviroment.clarityRecordInternal) {
    if (enviroment.clarityConsentEnabled) writeInternalMarker(true);
    stopAnalytics();
    return;
  }
  if (enviroment.clarityConsentEnabled && readInternalMarker()) {
    // A non-internal user in a browser an internal user left the marker
    // in: clear it and load now on a stored "granted" (deferred by the
    // bootstrap and activateAnalytics until this moment).
    writeInternalMarker(false);
    applyStoredConsent();
  }
  claritySet("rolle", identity.rolle);
  claritySet("forhandler", identity.forhandler);
  claritySet("kunde", identity.kunde);
  claritySet("sprog", identity.sprog);
  if (identity.kmSession) claritySet("km_session", identity.kmSession);
  clarityIdentify(identity.customId, identity.friendlyName);
}

// Called on every pathname change (design B13): tags and identify are
// page-bound in Clarity, so the last identity is sent again for the new page.
export function resendIdentity(): void {
  if (!lastIdentity) return;
  applyIdentity(lastIdentity);
}

export function clarityEvent(name: ClarityEventName): void {
  if (!enviroment.clarityEventsEnabled) return;
  if (!isClarityEnabled()) return;
  callClarity("event", name);
}

export function __resetClarityForTests(): void {
  dedupePathname = null;
  sentOnPage.clear();
  lastIdentity = null;
  loadRequested = false;
  analyticsStopped = false;
  withdrawnThisPageView = false;
  setGaDisabled(false);
}
